<template>
  <div class="psCriteriaFilters">
    <div
      v-for="field in activeFields"
      :key="fieldIdentity(field)"
      class="column criteriaField"
      :data-group="field.group"
      :style="fieldStyle(field)">
      <div class="criteriaFieldHeader">
        <div class="label">{{field.label}}</div>
        <el-button
          v-if="!isFixed(field)"
          circle
          plain
          type="danger"
          size="mini"
          icon="el-icon-close"
          title="Quitar filtro"
          @click="removeFilter(fieldIdentity(field))">
        </el-button>
      </div>
      <ps-criteria-control
        :field="field"
        :value="values[fieldIdentity(field)]"
        @input="$set(values, fieldIdentity(field), $event)"
        @remote-error="$emit('remote-error', $event)">
      </ps-criteria-control>
    </div>
    <div class="criteriaActions">
      <el-dropdown trigger="click" :disabled="availableFields.length === 0" @command="addFilter">
        <el-button
          round
          type="info"
          size="mini"
          icon="el-icon-plus"
          :disabled="availableFields.length === 0"
          :title="availableFields.length === 0 ? 'No hay filtros disponibles' : ''">
          Agregar filtro<span v-if="activeValuedFilterCount"> ({{activeValuedFilterCount}})</span>
        </el-button>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item
            v-for="entry in dropdownEntries"
            :key="entry.key"
            :command="entry.field ? fieldIdentity(entry.field) : undefined"
            :disabled="entry.type === 'group'"
            :class="{ criteriaGroupLabel: entry.type === 'group', criteriaGroupedField: entry.grouped }">
            {{entry.type === 'group' ? entry.group : entry.field.label}}
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
      <span class="criteriaSpacer"></span>
      <div class="criteriaButtons">
        <el-button
          v-if="hasResults"
          round
          type="success"
          size="mini"
          icon="el-icon-printer"
          :loading="printing"
          @click="$emit('print')">
          Imprimir
        </el-button>
        <el-button round type="danger" size="mini" icon="el-icon-delete" @click="clean">Limpiar</el-button>
        <el-button round type="primary" size="mini" @click="apply">Filtrar</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import PsCriteriaControl from '@/shared/components/PsCriteriaControl.vue'
import { VALUELESS_OPERATORS, buildCriteria, fieldIdentity, hasValue, initialCriteriaValues, serializeCriteria } from '@/shared/criteria/criteria_utils'

export default {
  name: 'PsCriteriaFilters',
  components: { PsCriteriaControl },
  props: {
    config: { type: Object, required: true },
    hasResults: { type: Boolean, default: false },
    printing: { type: Boolean, default: false }
  },
  data () {
    return {
      values: initialCriteriaValues(this.config),
      activeFieldIds: this.fixedFields(this.config).map(field => fieldIdentity(field))
    }
  },
  computed: {
    activeFields () {
      return (this.config.fields || [])
        .filter(field => this.activeFieldIds.indexOf(fieldIdentity(field)) !== -1)
        .slice()
        .sort((a, b) => (a.order === undefined ? 0 : a.order) - (b.order === undefined ? 0 : b.order))
    },
    availableFields () {
      return (this.config.fields || [])
        .filter(field => !this.isFixed(field) && this.activeFieldIds.indexOf(fieldIdentity(field)) === -1)
        .slice()
        .sort((a, b) => String(a.label).localeCompare(String(b.label)))
    },
    dropdownEntries () {
      if (!this.availableFields.some(field => field.group)) {
        return this.availableFields.map(field => ({ type: 'field', key: `field:${fieldIdentity(field)}`, field, grouped: false }))
      }
      const groups = []
      this.availableFields.forEach(field => {
        const name = field.group || ''
        let group = groups.find(item => item.name === name)
        if (!group) {
          group = { name, fields: [] }
          groups.push(group)
        }
        group.fields.push(field)
      })
      return groups.reduce((entries, group) => {
        if (group.name) entries.push({ type: 'group', key: `group:${group.name}`, group: group.name })
        group.fields.forEach(field => entries.push({ type: 'field', key: `field:${fieldIdentity(field)}`, field, grouped: Boolean(group.name) }))
        return entries
      }, [])
    },
    activeValuedFilterCount () {
      return this.activeFields.filter(field => hasValue(this.values[fieldIdentity(field)]) || VALUELESS_OPERATORS.indexOf(field.operator) !== -1).length
    }
  },
  watch: {
    config: {
      deep: false,
      handler (config) {
        this.values = initialCriteriaValues(config)
        this.activeFieldIds = this.fixedFields(config).map(field => fieldIdentity(field))
      }
    }
  },
  methods: {
    fieldIdentity,
    isFixed (field) {
      return Boolean(field.fixed || field.isFixed)
    },
    fixedFields (config) {
      return (config.fields || []).filter(this.isFixed)
    },
    fieldStyle (field) {
      if (!field.span) return null
      return { flexBasis: `calc(${Math.min(24, Math.max(1, field.span)) / 24 * 100}% - 20px)` }
    },
    addFilter (identity) {
      const field = (this.config.fields || []).find(item => fieldIdentity(item) === identity)
      if (field && !this.isFixed(field) && this.activeFieldIds.indexOf(identity) === -1) this.activeFieldIds.push(identity)
    },
    removeFilter (identity) {
      const field = (this.config.fields || []).find(item => fieldIdentity(item) === identity)
      if (!field || this.isFixed(field)) return
      this.activeFieldIds = this.activeFieldIds.filter(id => id !== identity)
      this.$set(this.values, identity, initialCriteriaValues({ fields: [field] })[identity])
    },
    apply () {
      const activeConfig = Object.assign({}, this.config, { fields: this.activeFields })
      this.$emit('filter', serializeCriteria(buildCriteria(activeConfig, this.values)))
    },
    clean () {
      this.values = initialCriteriaValues(this.config)
      this.activeFieldIds = this.fixedFields(this.config).map(field => fieldIdentity(field))
      this.apply()
    }
  }
}
</script>

<style scoped>
.psCriteriaFilters { display: flex; align-items: flex-end; flex-wrap: wrap; padding: 15px; border: 1px solid lightgray; box-sizing: border-box; }
.criteriaField { margin: 0 20px 10px 0; min-width: 180px; }
.criteriaFieldHeader { display: flex; align-items: center; justify-content: space-between; min-height: 25px; }
.criteriaFieldHeader .label { margin-right: 8px; }
.criteriaFieldHeader .el-button { padding: 4px; }
.criteriaActions { display: flex; align-items: center; width: 100%; margin-top: 5px; }
.criteriaSpacer { flex: 1; }
.criteriaButtons { display: flex; padding-bottom: 1px; }
.criteriaGroupLabel { font-weight: 600; color: #606266; }
.criteriaGroupedField { padding-left: 32px; }

@media (max-width: 991.98px) {
  .psCriteriaFilters { align-items: stretch; }
  .criteriaField { width: 100%; margin: 0 0 10px; flex-basis: 100% !important; }
  .criteriaActions { flex-wrap: wrap; }
  .criteriaSpacer { display: none; }
  .criteriaButtons { margin-left: auto; }
}
</style>
